import React from 'react';
import {
	type UIAnalyticsEvent,
	AnalyticsListener,
	type UIAnalyticsEventHandler,
} from '@atlaskit/analytics-next';
import {
	type GasPureScreenEventPayload,
	type GasPurePayload,
	UI_EVENT_TYPE,
	OPERATIONAL_EVENT_TYPE,
	TRACK_EVENT_TYPE,
	SCREEN_EVENT_TYPE,
} from '@atlaskit/analytics-gas-types';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import { type ForgeUIAnalyticsContext, type GasV3Payload } from './types';

export const FORGE_UI_ANALYTICS_CHANNEL = 'forge-ui';

export interface AnalyticsWebClient {
	sendOperationalEvent: (event: GasPurePayload) => void;
	sendScreenEvent: (event: GasPureScreenEventPayload) => void;
	sendTrackEvent: (event: GasPurePayload) => void;
	sendUIEvent: (event: GasPurePayload) => void;
}

export const sendEvent =
	(client: AnalyticsWebClient | Promise<AnalyticsWebClient>) =>
	(event: GasV3Payload): void => {
		switch (event.eventType) {
			case UI_EVENT_TYPE:
				Promise.resolve(client).then((c) => c.sendUIEvent(event));
				break;

			case OPERATIONAL_EVENT_TYPE:
				Promise.resolve(client).then((c) => c.sendOperationalEvent(event));
				break;

			case TRACK_EVENT_TYPE:
				Promise.resolve(client).then((c) => c.sendTrackEvent(event));
				break;

			case SCREEN_EVENT_TYPE:
				Promise.resolve(client).then((c) => c.sendScreenEvent(event));
				break;
		}
	};

interface ForgeUIAnalyticsListenerProps {
	/** @deprecated use commonAttributes instead */
	actionSubjectId?: string;
	children: React.ReactNode;
	client: AnalyticsWebClient | Promise<AnalyticsWebClient>;
	commonAttributes?: Record<string, string>;
}

/** extract the attributes from event context which should be in the top-level payload */
function getPayloadAttributesFromContext(
	contexts: UIAnalyticsEvent['context'],
): ForgeUIAnalyticsContext {
	return (
		(contexts.find((obj) => obj && obj.forgeUIAnalyticsContext) ?? {}).forgeUIAnalyticsContext ?? {}
	);
}

// Merge forgeUIAttributes from outermost to innermost analytics contexts, so child contexts can
// override parent values in the final payload. If an inner context replaces extensionKey, keep the
// previous extensionKey separately so embedded macro events can report it as parentExtensionKey.
function getForgeUIAttributesFromContext(contexts: UIAnalyticsEvent['context']): {
	forgeUIAttributes: Record<string, unknown>;
	overriddenExtensionKey?: unknown;
} {
	return contexts.reduce<{
		forgeUIAttributes: Record<string, unknown>;
		overriddenExtensionKey?: unknown;
	}>(
		(prev, curr) => {
			// Only extract attributes passed as an object via the forgeUIAttributes key
			const forgeUIAttributes =
				curr && curr.forgeUIAttributes && typeof curr.forgeUIAttributes === 'object'
					? (curr.forgeUIAttributes as Record<string, unknown>)
					: {};
			const previousExtensionKey = prev.forgeUIAttributes.extensionKey;
			const nextExtensionKey = forgeUIAttributes.extensionKey;
			// Capture the first extensionKey that gets replaced by a nested context. That replaced
			// value is the parent macro key once a child renderer context contributes its own key.
			const overriddenExtensionKey =
				prev.overriddenExtensionKey ??
				(previousExtensionKey && nextExtensionKey && previousExtensionKey !== nextExtensionKey
					? previousExtensionKey
					: undefined);

			return {
				forgeUIAttributes: {
					...prev.forgeUIAttributes,
					...forgeUIAttributes,
				},
				overriddenExtensionKey,
			};
		},
		{ forgeUIAttributes: {} },
	);
}

const ForgeUIAnalyticsListener = ({
	children,
	client,
	commonAttributes = {},
}: ForgeUIAnalyticsListenerProps): React.JSX.Element => {
	const listenerHandler: UIAnalyticsEventHandler = (event) => {
		const { eventType, data } = event.payload;
		const {
			action,
			actionSubject,
			actionSubjectId,
			attributes,
			tags,
			source: sourceFromEvent,
		} = data;

		const forgeUIAnalyticsContext = getPayloadAttributesFromContext(event.context);
		const source = sourceFromEvent ?? forgeUIAnalyticsContext.source ?? 'unknown';
		// Use the merged nested context attributes and add parentExtensionKey for embedded macro
		// events that override extensionKey.
		let nestingAnalyticsAttributes: Record<string, unknown> | undefined;
		if (FeatureGates.getExperimentValue('platform_forge_nesting_analytics', 'isEnabled', false)) {
			const { forgeUIAttributes, overriddenExtensionKey } = getForgeUIAttributesFromContext(
				event.context,
			);
			let parentExtensionKey = {};
			if (
				action === 'rendered' &&
				actionSubject === 'forge.ui.renderer' &&
				forgeUIAttributes.isEmbeddedMacro &&
				overriddenExtensionKey
			) {
				// Child Forge renderer contexts replace extensionKey with the child macro key, so the
				// parent key is the previous extensionKey captured during context merging.
				parentExtensionKey = { parentExtensionKey: overriddenExtensionKey };
			} else if (
				action === 'rendered' &&
				actionSubject === 'forge.ui.embedded_macro' &&
				attributes?.extensionKey &&
				forgeUIAttributes.extensionKey
			) {
				// Embedded macro events carry the child macro key on the event attributes, while the
				// surrounding context still carries the parent macro extensionKey.
				parentExtensionKey = { parentExtensionKey: forgeUIAttributes.extensionKey };
			}

			nestingAnalyticsAttributes = {
				...forgeUIAttributes,
				...parentExtensionKey,
			};
		}

		const payload = {
			action,
			actionSubject,
			actionSubjectId,
			...forgeUIAnalyticsContext,
			source,
			attributes: {
				...commonAttributes,
				...(nestingAnalyticsAttributes ??
					event.context.reduce((prev, curr) => {
						// Only extract attributes passed as an object via the forgeUIAttributes key
						const forgeUIAttributes =
							curr && curr.forgeUIAttributes && typeof curr.forgeUIAttributes === 'object'
								? curr.forgeUIAttributes
								: {};
						return {
							...prev,
							...forgeUIAttributes,
						};
					}, {})),
				...attributes,
			},
			tags,
		};

		sendEvent(client)({ eventType: eventType, ...payload });
	};

	return (
		<AnalyticsListener onEvent={listenerHandler} channel={FORGE_UI_ANALYTICS_CHANNEL}>
			{children}
		</AnalyticsListener>
	);
};

export default ForgeUIAnalyticsListener;
